import { useEffect, useMemo, useState } from 'react';
import { initializeApp, deleteApp } from 'firebase/app';
import { createUserWithEmailAndPassword, getAuth, signOut as signOutSecondary } from 'firebase/auth';
import {
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  getDocs,
  writeBatch
} from 'firebase/firestore';
import {
  Building2,
  Edit3,
  Plus,
  Search,
  PauseCircle,
  PlayCircle,
  Archive,
  CreditCard,
  MonitorPlay,
  Users,
  CalendarDays
} from 'lucide-react';
import { db, firebaseConfig } from '../../lib/firebase';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import PageHeader from '../../components/PageHeader';
import Card from '../../components/Card';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';
import DataTable from '../../components/DataTable';
import StatCard from '../../components/StatCard';
import { formatDate, screenLimitLabel } from '../../utils/format';
import { activateSubscription, suspendTenant, syncTenantScreenEntitlements } from '../../services/subscriptionService';

const blank = {
  companyName: '',
  contactName: '',
  email: '',
  phone: '',
  password: '',
  address: '',
  planId: ''
};

export default function TenantsPage() {
  const { authUser } = useAuth();
  const { notify } = useToast();
  const [companies, setCompanies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [screens, setScreens] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const unsubCompanies = onSnapshot(collection(db, 'tenants'), snap => {
      setCompanies(snap.docs.map(item => ({ id: item.id, ...item.data() })).filter(item => item.id !== 'super_admin'));
    });
    const unsubPlans = onSnapshot(query(collection(db, 'plans'), where('status', '==', 'active')), snap => {
      setPlans(snap.docs.map(item => ({ id: item.id, ...item.data() })));
    });
    const unsubScreens = onSnapshot(collection(db, 'screens'), snap => setScreens(snap.docs.map(item => ({ id: item.id, ...item.data() }))));
    const unsubUsers = onSnapshot(collection(db, 'users'), snap => setUsers(snap.docs.map(item => ({ id: item.id, ...item.data() }))));
    return () => { unsubCompanies(); unsubPlans(); unsubScreens(); unsubUsers(); };
  }, []);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return companies
      .filter(company => statusFilter === 'all' || String(company.status || 'active').toLowerCase() === statusFilter)
      .filter(company => !needle || [company.companyName, company.contactName, company.email, company.phone].some(value => String(value || '').toLowerCase().includes(needle)))
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
  }, [companies, search, statusFilter]);

  const activeCount = companies.filter(item => String(item.status || '').toLowerCase() === 'active').length;
  const suspendedCount = companies.filter(item => String(item.status || '').toLowerCase() === 'suspended').length;
  const pendingCount = companies.filter(item => String(item.subscriptionStatus || '').toLowerCase() !== 'active' && String(item.status || '').toLowerCase() !== 'deleted').length;

  function screenCount(tenantId) {
    return screens.filter(item => item.tenantId === tenantId && !['deleted', 'removed'].includes(String(item.status || '').toLowerCase())).length;
  }

  function userCount(tenantId) {
    return users.filter(item => item.tenantId === tenantId && String(item.status || 'active').toLowerCase() !== 'deleted').length;
  }

  function createCompany() {
    setEditing(null);
    setForm(blank);
    setOpen(true);
  }

  function editCompany(company) {
    setEditing(company);
    setForm({
      companyName: company.companyName || '',
      contactName: company.contactName || '',
      email: company.email || '',
      phone: company.phone || '',
      password: '',
      address: company.address || '',
      planId: ''
    });
    setOpen(true);
  }

  async function saveCompany(event) {
    event.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        await updateDoc(doc(db, 'tenants', editing.id), {
          companyName: form.companyName.trim(),
          contactName: form.contactName.trim(),
          phone: form.phone.trim(),
          address: form.address.trim(),
          updatedAt: serverTimestamp()
        });
        notify('Company updated.', 'success');
        setOpen(false);
        return;
      }

      const secondary = initializeApp(firebaseConfig, `tenant-create-${Date.now()}`);
      const secondaryAuth = getAuth(secondary);
      try {
        const credential = await createUserWithEmailAndPassword(secondaryAuth, form.email.trim(), form.password);
        const tenantRef = doc(collection(db, 'tenants'));
        await setDoc(tenantRef, {
          id: tenantRef.id,
          companyName: form.companyName.trim(),
          contactName: form.contactName.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          address: form.address.trim(),
          status: 'pending_subscription',
          subscriptionStatus: 'inactive',
          maxScreens: 0,
          maxStorageGB: 0,
          ownerUid: credential.user.uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        await setDoc(doc(db, 'users', credential.user.uid), {
          uid: credential.user.uid,
          tenantId: tenantRef.id,
          name: form.contactName.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          role: 'owner',
          status: 'active',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        await setDoc(doc(collection(db, 'screen_groups')), {
          tenantId: tenantRef.id,
          name: 'Default Group',
          createdAt: serverTimestamp()
        });

        const plan = plans.find(item => item.id === form.planId);
        if (plan) {
          await activateSubscription({
            tenantId: tenantRef.id,
            plan,
            activatedBy: authUser?.uid || ''
          });
        }
        notify(plan ? 'Company created and subscription activated.' : 'Company created. Subscription is required before screens can play.', 'success');
        setOpen(false);
      } finally {
        try { await signOutSecondary(secondaryAuth); } catch (_) { /* ignore */ }
        await deleteApp(secondary);
      }
    } catch (error) {
      notify(String(error?.message || error), 'error');
    } finally {
      setBusy(false);
    }
  }

  async function toggleSuspend(company) {
    const suspended = String(company.status || '').toLowerCase() === 'suspended';
    if (!window.confirm(`${suspended ? 'Restore' : 'Suspend'} ${company.companyName}?`)) return;
    try {
      await suspendTenant(company.id, !suspended);
      notify(suspended ? 'Company restored.' : 'Company suspended. Its TV screens will be locked.', 'success');
    } catch (error) {
      notify(String(error?.message || error), 'error');
    }
  }

  async function archiveCompany(company) {
    if (!window.confirm(`Archive ${company.companyName}? This will disable company users and signage access.`)) return;
    try {
      const companyUsers = await getDocs(query(collection(db, 'users'), where('tenantId', '==', company.id)));
      const batch = writeBatch(db);
      batch.update(doc(db, 'tenants', company.id), {
        status: 'deleted',
        subscriptionStatus: 'cancelled',
        archivedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      companyUsers.docs.forEach(item => batch.update(item.ref, { status: 'disabled', updatedAt: serverTimestamp() }));
      await batch.commit();
      await syncTenantScreenEntitlements(company.id, 0, false);
      notify('Company archived and signage access disabled.', 'success');
    } catch (error) {
      notify(String(error?.message || error), 'error');
    }
  }

  const columns = [
    {
      key: 'company', label: 'Company', render: company => (
        <div className="table-primary"><div className="company-avatar"><Building2 size={18} /></div><div><strong>{company.companyName || 'Unnamed company'}</strong><small>{company.email || '—'}</small></div></div>
      )
    },
    { key: 'contact', label: 'Contact', render: company => <div><strong>{company.contactName || '—'}</strong><small className="block-muted">{company.phone || 'No phone'}</small></div> },
    { key: 'status', label: 'Status', render: company => {
      const value = String(company.status || 'active').toLowerCase();
      return <Badge tone={value === 'active' ? 'success' : value === 'suspended' || value === 'deleted' ? 'danger' : 'warning'}>{value.replace('_', ' ')}</Badge>;
    } },
    { key: 'subscription', label: 'Subscription', render: company => <div><Badge tone={company.subscriptionStatus === 'active' ? 'success' : 'warning'}>{company.subscriptionStatus || 'inactive'}</Badge><small className="block-muted">{company.currentPlanName || 'No package'}</small></div> },
    { key: 'screens', label: 'Screens', render: company => <strong>{screenCount(company.id)} / {screenLimitLabel(company.maxScreens || 0)}</strong> },
    { key: 'users', label: 'Users', render: company => <strong>{userCount(company.id)}</strong> },
    { key: 'expiry', label: 'Expiry', render: company => formatDate(company.subscriptionExpiry) },
    {
      key: 'actions', label: 'Actions', render: company => (
        <div className="table-actions">
          <button className="icon-btn" onClick={() => editCompany(company)} title="Edit company"><Edit3 size={16} /></button>
          <button className="icon-btn" onClick={() => toggleSuspend(company)} title={company.status === 'suspended' ? 'Restore' : 'Suspend'}>{company.status === 'suspended' ? <PlayCircle size={16} /> : <PauseCircle size={16} />}</button>
          <a className="icon-btn" href={`/superadmin/subscriptions?tenant=${company.id}`} title="Manage subscription"><CreditCard size={16} /></a>
          <button className="icon-btn danger" onClick={() => archiveCompany(company)} title="Archive company"><Archive size={16} /></button>
        </div>
      )
    }
  ];

  return (
    <>
      <PageHeader
        eyebrow="Platform management"
        title="Companies"
        description="Create tenant companies, manage owners, monitor screen usage and control company access."
        actions={<button className="btn btn-primary" onClick={createCompany}><Plus size={17} /> Add company</button>}
      />

      <div className="stats-grid four">
        <StatCard icon={Building2} label="Companies" value={companies.filter(item => item.status !== 'deleted').length} detail={`${activeCount} active`} tone="blue" />
        <StatCard icon={MonitorPlay} label="Screens" value={screens.filter(item => !['deleted', 'removed'].includes(String(item.status || '').toLowerCase())).length} detail="Across all tenants" tone="green" />
        <StatCard icon={Users} label="Tenant users" value={users.filter(item => item.tenantId && item.tenantId !== 'super_admin').length} detail="Owners and staff" tone="purple" />
        <StatCard icon={CalendarDays} label="Needs subscription" value={pendingCount} detail={`${suspendedCount} suspended`} tone="amber" />
      </div>

      <Card>
        <div className="toolbar-row">
          <div className="search-box"><Search size={17} /><input value={search} onChange={event => setSearch(event.target.value)} placeholder="Search company, owner, email or phone…" /></div>
          <select className="compact-select" value={statusFilter} onChange={event => setStatusFilter(event.target.value)}>
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="pending_subscription">Pending subscription</option>
            <option value="suspended">Suspended</option>
            <option value="deleted">Archived</option>
          </select>
        </div>
        <DataTable columns={columns} rows={filtered} empty="No companies match the current filters." />
      </Card>

      <Modal
        open={open}
        onClose={() => !busy && setOpen(false)}
        title={editing ? 'Edit company' : 'Create company'}
        description={editing ? 'Update company information.' : 'Creates the tenant plus its owner login. A package can be activated now or purchased later.'}
        size="lg"
        footer={<><button className="btn btn-soft" onClick={() => setOpen(false)} disabled={busy}>Cancel</button><button className="btn btn-primary" form="companyForm" disabled={busy}>{busy ? 'Saving…' : editing ? 'Save company' : 'Create company'}</button></>}
      >
        <form id="companyForm" className="form-grid two-col" onSubmit={saveCompany}>
          <label className="field"><span>Company name</span><input value={form.companyName} onChange={event => setForm({ ...form, companyName: event.target.value })} required /></label>
          <label className="field"><span>Contact / owner name</span><input value={form.contactName} onChange={event => setForm({ ...form, contactName: event.target.value })} required /></label>
          <label className="field"><span>Email</span><input type="email" value={form.email} onChange={event => setForm({ ...form, email: event.target.value })} required disabled={Boolean(editing)} /></label>
          <label className="field"><span>Phone</span><input value={form.phone} onChange={event => setForm({ ...form, phone: event.target.value })} /></label>
          <label className="field form-full"><span>Address</span><textarea rows="3" value={form.address} onChange={event => setForm({ ...form, address: event.target.value })} /></label>
          {!editing && <>
            <label className="field"><span>Owner password</span><input type="password" minLength="6" value={form.password} onChange={event => setForm({ ...form, password: event.target.value })} required /></label>
            <label className="field"><span>Initial package (optional)</span><select value={form.planId} onChange={event => setForm({ ...form, planId: event.target.value })}><option value="">No package — customer purchases later</option>{plans.map(plan => <option key={plan.id} value={plan.id}>{plan.name} · {screenLimitLabel(plan.screens)} screens</option>)}</select></label>
          </>}
        </form>
      </Modal>
    </>
  );
}
